import { useMutation } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { DISABLE_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import type { TAppData } from '../apps-data'

interface IDisableAppModalProps {
  app: TAppData
  isOpen: boolean
  onClose: () => void
}

const InitialWarning = ({
  onClose,
  handleSetShowWarning
}: {
  handleSetShowWarning: () => void
  onClose: () => void
}) => {
  return (
    <>
      <DialogDescription className="pt-2">Do you want to disable it?</DialogDescription>
      <DialogFooter className="sm:justify-start">
        <Button onClick={handleSetShowWarning} className="mr-2">
          Disable
        </Button>
        <Button variant="outline" onClick={onClose}>
          No
        </Button>
      </DialogFooter>
    </>
  )
}

const FinalWarning = ({
  onClose,
  handleDisablingApp,
  loading
}: {
  handleDisablingApp: () => void
  loading: boolean
  onClose: () => void
}) => {
  return (
    <DialogFooter className="sm:justify-start">
      <Button variant="destructive" onClick={handleDisablingApp} disabled={loading} className="mr-2">
        {loading ? 'Disabling...' : 'Yes'}
      </Button>
      <Button variant="outline" onClick={onClose}>
        Close
      </Button>
    </DialogFooter>
  )
}

/**
 * This is the modal for the DisableAppModal app.
 * It is used to disable an app.
 */
export function DisableAppModal({ app, isOpen, onClose }: IDisableAppModalProps) {
  const [showWarning, setShowWarning] = useState(false)

  const [disableApp, { loading }] = useMutation(DISABLE_APP, {
    onCompleted: () => {
      onClose()
      toast.success(`${app.appName} has been disabled successfully.`)
    },
    onError: (err) => {
      toast.error(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
      onClose()
    },
    refetchQueries: [GET_APPS]
  })

  const handleDisablingApp = () => {
    disableApp({
      context: {
        headers: {
          'ws-id': app.workspaceID || ''
        }
      },
      variables: { appType: app.appType }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{app.appName} is currently enabled</DialogTitle>
          {showWarning ? (
            <FinalWarning onClose={onClose} handleDisablingApp={handleDisablingApp} loading={loading} />
          ) : (
            <InitialWarning onClose={onClose} handleSetShowWarning={() => setShowWarning(true)} />
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}
